import { createSelector } from '@ngrx/store';
import {
   getCurrentUserIsSystemUser,
   hasSelectedPrivilege,
   selectPrivileges,
} from '../authentication/state/authentication.selector';
import { AccountPrivileges } from '../pages/account-details/models/account-privileges';
import { LogLevelPrivileges } from '../pages/account-details/models/log-level-privileges';
import { ActiveCallsPrivileges } from '../pages/active-calls/models/active-calls-privileges';
import {
   CallRecordingsPrivileges,
   LCSCallRecordingsPrivileges,
} from '../pages/active-calls/models/call-recordings-privileges';
import { HangUpCallsPrivileges } from '../pages/active-calls/models/hang-up-calls.privileges';
import { CallCostPrivileges } from '../pages/call-cost-register/models/call-cost-privileges';
import { CallDetailsPrivileges } from '../pages/call-logs-register/models/call-details-privileges';
import { DepartmentPrivileges } from '../pages/department-register/models/department-privileges';
import { DevicePrivileges } from '../pages/device-register/models/device-privileges';
import { ExtensionPrivileges } from '../pages/extension-details/models/extension-privileges';
import { ExtensionGroupPrivileges } from '../pages/extension-group-details/models/extension-group-privileges';
import { LocationPrivileges } from '../pages/locations-register-component/models/location-privileges';
import { MailboxPrivileges } from '../pages/mailbox-register/models/mailbox-privileges';
import { PhoneNumberPrivileges } from '../pages/phone-number-details/models/phone-number-privileges';
import { ExtensionPhonePrivileges } from '../pages/phones/extension-phone-register/models/extension-phone-privileges';
import { PhonePrivileges } from '../pages/phones/models/phone-privileges';
import { EditPresenceContactPrivileges } from '../pages/presence-contact-register/models/presence-contact-privileges';
import { PromptPrivileges } from '../pages/prompt-register/models/prompt-privileges';
import { RolePrivileges } from '../pages/role-register/models/role-privileges';
import { SchedulePrivileges } from '../pages/schedule-register/models/schedule-privileges';
import { UserPrivileges } from '../pages/user-details/models/user-privileges';
import { getIsOnSystemPage, getSelectedAccountId } from '../store/select-account/select-account.selector';
import { Privileges } from './privileges';
export const getExtensionPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): ExtensionPrivileges => {
      const canAddAutoAttendants = hasSelectedPrivilege(
         Privileges.ManageExtensionAutoAttendants_Add,
         accountId,
         privileges
      );
      const canAddFaxes = hasSelectedPrivilege(Privileges.ManageExtensionFaxes_Add, accountId, privileges);
      const canAddOthers = hasSelectedPrivilege(Privileges.ManageOtherExtensions_Add, accountId, privileges);
      const canAddPhones = hasSelectedPrivilege(Privileges.ManageExtensionPhones_Add, accountId, privileges);
      const canAddFaxEmails = hasSelectedPrivilege(Privileges.ManageFaxEmailAddresses_Add, accountId, privileges);
      const canDeleteAutoAttendants = hasSelectedPrivilege(
         Privileges.ManageExtensionAutoAttendants_Delete,
         accountId,
         privileges
      );
      const canDeleteFaxes = hasSelectedPrivilege(Privileges.ManageExtensionFaxes_Delete, accountId, privileges);
      const canDeleteFaxEmails = hasSelectedPrivilege(Privileges.ManageFaxEmailAddresses_Delete, accountId, privileges);
      const canDeleteOthers = hasSelectedPrivilege(Privileges.ManageOtherExtensions_Delete, accountId, privileges);
      const canDeletePhones = hasSelectedPrivilege(
         Privileges.ManageExtensionPhoneSettings_Delete,
         accountId,
         privileges
      );
      const canEditAutoAttendants = hasSelectedPrivilege(
         Privileges.ManageExtensionAutoAttendants_Edit,
         accountId,
         privileges
      );
      const canEditDepartmentExtensions = hasSelectedPrivilege(
         Privileges.ManageDepartmentExtensions_Edit,
         accountId,
         privileges
      );
      const canEditFaxes = hasSelectedPrivilege(Privileges.ManageExtensionFaxes_Edit, accountId, privileges);
      const canEditFaxEmails = hasSelectedPrivilege(Privileges.ManageFaxEmailAddresses_Edit, accountId, privileges);
      const isManageSuperviseesEnabled = hasSelectedPrivilege(
         Privileges.ManageExtensionSupervisees_Enabled,
         accountId,
         privileges
      );
      const canEditOthers = hasSelectedPrivilege(Privileges.ManageOtherExtensions_Edit, accountId, privileges);
      const canEditPhones = hasSelectedPrivilege(Privileges.ManageExtensionPhoneSettings_Edit, accountId, privileges);
      const enabled = hasSelectedPrivilege(Privileges.ManageExtensions_Enabled, accountId, privileges);
      return {
         canAddAutoAttendants,
         canAddFaxes,
         canAddFaxEmails,
         canAddOthers,
         canAddPhones,
         canDeleteAutoAttendants,
         canDeleteFaxes,
         canDeleteFaxEmails,
         canDeleteOthers,
         canDeletePhones,
         canEditAutoAttendants,
         canEditFaxes,
         canEditFaxEmails,
         canEditOthers,
         canEditPhones,
         canEditDepartmentExtensions,
         enabled,
         isManageSuperviseesEnabled,
      };
   }
);

export const getUserPrivileges = createSelector(
   getSelectedAccountId,
   getIsOnSystemPage,
   selectPrivileges,
   (accountId, isOnSystemPage, privileges): UserPrivileges => {
      const canAdd = hasSelectedPrivilege(
         isOnSystemPage ? Privileges.ManageSystemUsers_Add : Privileges.ManageUsersAndPrivileges_Add,
         accountId,
         privileges
      );
      const canDelete = hasSelectedPrivilege(
         isOnSystemPage ? Privileges.ManageSystemUsers_Delete : Privileges.ManageUsersAndPrivileges_Delete,
         accountId,
         privileges
      );
      const canEdit = hasSelectedPrivilege(
         isOnSystemPage ? Privileges.ManageSystemUsers_Edit : Privileges.ManageUsersAndPrivileges_Edit,
         accountId,
         privileges
      );
      return {
         canAdd,
         canDelete,
         canEdit,
      };
   }
);

export const getAccountPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): AccountPrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageAccounts_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageAccounts_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageAccountSettings_Edit, accountId, privileges);
      const canRebootPhones = hasSelectedPrivilege(Privileges.RebootPhones_Enabled, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canRebootPhones,
      };
   }
);

export const getPhoneNumberPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): PhoneNumberPrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManagePhoneNumbers_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManagePhoneNumbers_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManagePhoneNumberSettings_Edit, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
      };
   }
);

export const getRolePrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): RolePrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageRoles_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageRoles_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageRoles_Edit, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
      };
   }
);

export const getPhonePrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): PhonePrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageExtensionPhones_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageExtensionPhoneSettings_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageExtensionPhoneSettings_Edit, accountId, privileges);
      const canView = hasSelectedPrivilege(Privileges.ManageExtensions_Enabled, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canView,
      };
   }
);

export const getExtensionPhonePrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): ExtensionPhonePrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageExtensionPhones_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageExtensionPhoneSettings_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageExtensionPhoneSettings_Edit, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
      };
   }
);

export const getPromptPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): PromptPrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageAccountPrompts_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageAccountPrompts_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageAccountPrompts_Edit, accountId, privileges);
      const canView = hasSelectedPrivilege(Privileges.ManageAccountPrompts_View, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canView,
      };
   }
);

export const getDevicePrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): DevicePrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageDevices_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageDevices_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageDevices_Edit, accountId, privileges);
      const canView = hasSelectedPrivilege(Privileges.ManageDevices_View, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canView,
      };
   }
);

export const getLocationPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   getCurrentUserIsSystemUser,
   (accountId, privileges, isSystemUser): LocationPrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageLocations_Add, accountId, privileges) && isSystemUser;
      const canDelete = hasSelectedPrivilege(Privileges.ManageLocations_Delete, accountId, privileges) && isSystemUser;
      const canEdit = hasSelectedPrivilege(Privileges.ManageLocations_Edit, accountId, privileges) && isSystemUser;
      const canView = hasSelectedPrivilege(Privileges.ManageLocationSettings_View, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canView,
      };
   }
);

export const getSchedulePrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): SchedulePrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageSchedules_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageSchedules_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageSchedules_Edit, accountId, privileges);
      const canView = hasSelectedPrivilege(Privileges.ManageSchedules_View, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canView,
      };
   }
);

export const getDepartmentPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): DepartmentPrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageDepartments_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageDepartments_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageDepartments_Edit, accountId, privileges);
      const canView = hasSelectedPrivilege(Privileges.ManageDepartments_View, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canView,
      };
   }
);

export const getProviderCallRatePrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): CallCostPrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageProviderCallRates_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageProviderCallRates_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageProviderCallRates_Edit, accountId, privileges);
      const canView = hasSelectedPrivilege(Privileges.ManageProviderCallRates_View, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canView,
      };
   }
);

export const getActiveCallsPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): ActiveCallsPrivileges => {
      const canView = hasSelectedPrivilege(Privileges.ManageActiveCalls_View, accountId, privileges);
      return {
         canView,
      };
   }
);

export const getHangUpCallsPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): HangUpCallsPrivileges => {
      const enabled = hasSelectedPrivilege(Privileges.HangUpCalls_Enabled, accountId, privileges);
      return {
         enabled,
      };
   }
);

export const getCallRecordingsPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): CallRecordingsPrivileges => {
      const enabled = hasSelectedPrivilege(Privileges.ManageCallRecordings_Enabled, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.DeleteCallRecordings_Delete, accountId, privileges);
      return {
         enabled,
         canDelete,
      };
   }
);

export const getCallRecordingsLCSPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): LCSCallRecordingsPrivileges => {
      const enabled = hasSelectedPrivilege(Privileges.ManageLCSCallRecordings_Enabled, accountId, privileges);
      return {
         enabled,
      };
   }
);

export const getBannedAllowedIPsPrivilege = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): LCSCallRecordingsPrivileges => {
      const enabled = hasSelectedPrivilege(Privileges.ManageBannedAllowedIps_Enabled, accountId, privileges);
      return {
         enabled,
      };
   }
);

export const getCallDetailsPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): CallDetailsPrivileges => {
      const canView = hasSelectedPrivilege(Privileges.ManageCallDetails_View, accountId, privileges);
      return {
         canView,
      };
   }
);

export const getExtensionGroupDetailsPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): ExtensionGroupPrivileges => {
      const canView = hasSelectedPrivilege(Privileges.ManageExtensions_Enabled, accountId, privileges);
      const canAdd = hasSelectedPrivilege(Privileges.ManageExtensionGroups_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageExtensionGroups_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageExtensionGroups_Edit, accountId, privileges);
      return {
         canView,
         canAdd,
         canDelete,
         canEdit,
      };
   }
);

export const getMailboxPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): MailboxPrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageOtherExtensions_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageOtherExtensions_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageOtherExtensions_Edit, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
      };
   }
);

export const getSystemLogPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): SchedulePrivileges => {
      const canAdd = hasSelectedPrivilege(Privileges.ManageSystemLogs_Add, accountId, privileges);
      const canDelete = hasSelectedPrivilege(Privileges.ManageSystemLogs_Delete, accountId, privileges);
      const canEdit = hasSelectedPrivilege(Privileges.ManageSystemLogs_Edit, accountId, privileges);
      const canView = hasSelectedPrivilege(Privileges.ManageSystemLogs_View, accountId, privileges);
      return {
         canAdd,
         canDelete,
         canEdit,
         canView,
      };
   }
);

export const getPresenceContactPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): EditPresenceContactPrivileges => {
      const canEdit = hasSelectedPrivilege(Privileges.ManagePresenceContacts_Edit, accountId, privileges);
      return {
         canEdit,
      };
   }
);

export const getLogLevelPrivileges = createSelector(
   getSelectedAccountId,
   selectPrivileges,
   (accountId, privileges): LogLevelPrivileges => {
      const enabled = hasSelectedPrivilege(Privileges.ManageLogLevels_Enabled, accountId, privileges);
      return {
         enabled,
      };
   }
);
